// reducers/authSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { showError } from '../../helpers/toast';
import { attachApiErrorToast, notifyApiSuccessFrom } from '../../helpers/apiNotify';
import axios from 'axios';
import {
  getMfaDeviceToken,
  saveMfaDeviceToken,
} from '../../helpers/mfaDeviceToken';

const API = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_URL
});
attachApiErrorToast(API, {
  fallback: 'Auth request failed',
  onUnauthorized: (error) => {
    // Do not wipe session / redirect on failed login or signup
    const url = String(error?.config?.url || '');
    if (
      url.includes('/auth/login') ||
      url.includes('/auth/register') ||
      url.includes('/users/signup') ||
      url.includes('/auth/otp') ||
      url.includes('/auth/mfa')
    ) {
      return;
    }
    // Do not clear mfa_device_token keys — trusted device must survive logout
    const deviceKeys = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith('mfa_device_token:')) deviceKeys.push(k);
    }
    const preserved = deviceKeys.map((k) => [k, localStorage.getItem(k)]);
    localStorage.clear();
    preserved.forEach(([k, v]) => {
      if (v) localStorage.setItem(k, v);
    });
    window.location.href = '/';
  },
});

/**
 * Live MFA contract (apiportal) — SMS_MFA Frontend API Changes:
 * POST /auth/login       { emailOrPhone, password, mfa_device_token? }
 * POST /auth/mfa/verify  { mfa_token, code, mfa_device_token? }
 * POST /auth/mfa/resend  { mfa_token }
 */
const MFA_VERIFY_PATH =
  process.env.REACT_APP_MFA_VERIFY_PATH || '/auth/mfa/verify';
const MFA_RESEND_PATH =
  process.env.REACT_APP_MFA_RESEND_PATH || '/auth/mfa/resend';

const persistDeviceTokenFromSession = (data, fallbackEmail) => {
  const token = data?.mfa_device_token;
  if (!token) return;
  saveMfaDeviceToken(
    data?.user?.email || fallbackEmail || null,
    token,
    data?.mfa_device_expires_at || data?.mfa_device_expiresAt || null
  );
};

/** Detect MFA challenge from login (no full access_token yet) */
export const isMfaLoginChallenge = (data) => {
  if (!data || typeof data !== 'object') return false;
  if (data.access_token && data.user) return false;
  if (
    data.mfa_required === true ||
    data.mfaRequired === true ||
    data.requires_otp === true ||
    data.requiresOtp === true ||
    data.otp_required === true ||
    data.otpRequired === true
  ) {
    return true;
  }
  // Backend uses mfa_token for the pending verification session
  if (data.mfa_token && !data.access_token) return true;
  const token =
    data.mfaToken ||
    data.session_token ||
    data.otp_token ||
    data.second_factor_token ||
    data.temp_token;
  if (token && !data.access_token) return true;
  return false;
};

const resolveCodeExpiresAt = (data) => {
  if (data?.code_expires_at || data?.codeExpiresAt) {
    return data.code_expires_at || data.codeExpiresAt;
  }
  const seconds = Number(data?.code_expires_in ?? data?.codeExpiresIn);
  if (Number.isFinite(seconds) && seconds > 0) {
    return new Date(Date.now() + seconds * 1000).toISOString();
  }
  return new Date(Date.now() + 600 * 1000).toISOString();
};

export const extractMfaChallenge = (data, loginEmail = null) => ({
  token: data?.mfa_token || data?.mfaToken || null,
  phoneHint:
    data?.phone_hint ||
    data?.phoneHint ||
    data?.masked_phone ||
    data?.phone ||
    null,
  message: data?.message || data?.reason || data?.error || null,
  loginEmail: loginEmail || null,
  codeExpiresAt: resolveCodeExpiresAt(data),
  codeExpiresIn: data?.code_expires_in ?? data?.codeExpiresIn ?? null,
  mfaTokenExp: data?.mfa_token_exp || data?.mfaTokenExp || null,
  raw: data,
});

const mfaErrorMessage = (error, fallback) => {
  const data = error?.response?.data;
  if (Array.isArray(data?.errors) && data.errors[0]?.msg) {
    return data.errors.map((e) => e.msg).join(', ');
  }
  return (
    data?.reason ||
    data?.message ||
    data?.error ||
    error?.message ||
    fallback
  );
};

const initialState = {
  user: null,
  loading: false,
  error: null,
  /** Pending SMS MFA after password login (testing results row 327) */
  mfa: null,
};

export const registerUser = createAsyncThunk('/auth/register', async (userData) => {
  try {
    const response = await API.post('/auth/register', userData);
    notifyApiSuccessFrom(response, 'Registration Successfully!');
    return response.data;
  } catch (error) {
    throw new Error('Failed to register user');
  }
});

export const loginUser = createAsyncThunk(
  '/auth/login',
  async (credentials, { rejectWithValue }) => {
    try {
      const loginEmail = credentials?.emailOrPhone;
      const deviceToken = getMfaDeviceToken(loginEmail);
      const payload = { ...credentials };
      if (deviceToken) {
        payload.mfa_device_token = deviceToken;
      }

      const response = await API.post('/auth/login', payload);
      const data = response.data;

      // SMS MFA challenge — do not treat as full session yet
      if (isMfaLoginChallenge(data)) {
        return {
          __mfa: true,
          challenge: extractMfaChallenge(data, loginEmail),
        };
      }

      if (data.user?.is_admin || data.user?.user_role) {
        persistDeviceTokenFromSession(data, loginEmail);
        notifyApiSuccessFrom(response, 'Login Successfully!');
        return data;
      }
      const deniedMsg =
        data?.reason ||
        data?.message ||
        data?.error ||
        'Login Failed: Insufficient permissions';
      showError(deniedMsg);
      return rejectWithValue(deniedMsg);
    } catch (error) {
      // Toast already shown by interceptor (API reason / network message)
      return rejectWithValue(
        error.response?.data?.reason ||
          error.response?.data?.message ||
          error.message ||
          'Failed to log in'
      );
    }
  }
);

/** Complete login after SMS OTP */
export const verifyLoginOtp = createAsyncThunk(
  'auth/verifyLoginOtp',
  async ({ otp, mfaToken, loginEmail }, { getState, rejectWithValue }) => {
    try {
      const email = loginEmail || getState()?.auth?.mfa?.loginEmail || null;
      const deviceToken = getMfaDeviceToken(email);
      const body = {
        mfa_token: mfaToken,
        code: String(otp).trim(),
      };
      if (deviceToken) {
        body.mfa_device_token = deviceToken;
      }

      const response = await API.post(MFA_VERIFY_PATH, body, {
        skipErrorToast: true,
      });
      const data = response.data;
      if (data?.user?.is_admin || data?.user?.user_role || data?.access_token) {
        persistDeviceTokenFromSession(data, email);
        notifyApiSuccessFrom(response, 'Login Successfully!');
        return data;
      }
      const deniedMsg =
        data?.reason ||
        data?.message ||
        data?.error ||
        'OTP verification failed';
      return rejectWithValue({
        message: deniedMsg,
        invalidOtp: true,
        codeExpired: false,
        expired: false,
      });
    } catch (error) {
      const message = mfaErrorMessage(error, 'Failed to verify OTP');
      const status = error.response?.status;
      const errorText = String(
        error.response?.data?.error ||
          error.response?.data?.message ||
          error.response?.data?.reason ||
          message ||
          ''
      );
      const lower = errorText.toLowerCase();
      // SMS code bad/expired — stay on OTP screen (never treat as login redirect)
      const codeExpired =
        /code expired|otp expired|verification code expired|expired code|request a new code|otp has expired|code has expired/i.test(
          lower
        );
      const invalidOtp =
        !codeExpired &&
        (/invalid|incorrect|wrong|does not match|mismatch|bad (otp|code)|one[- ]time/i.test(
          lower
        ) ||
          status === 400 ||
          status === 422 ||
          // Most verify failures are bad codes; only leave screen for clear MFA-token death
          (status === 401 &&
            !/mfa[_ ]?token|verification session|login session|sign in again/i.test(
              lower
            )));
      // Leave OTP screen only when MFA challenge token itself is dead
      const sessionExpired =
        !codeExpired &&
        !invalidOtp &&
        (/mfa[_ ]?token (expired|invalid|not found)|verification session expired|login session expired/i.test(
          lower
        ) ||
          (status === 401 &&
            /session expired|sign in again/i.test(errorText) &&
            !/otp|code|verification code/i.test(lower)));
      return rejectWithValue({
        message,
        status,
        expired: sessionExpired,
        codeExpired,
        invalidOtp: invalidOtp || (!sessionExpired && !codeExpired),
      });
    }
  }
);

export const resendLoginOtp = createAsyncThunk(
  'auth/resendLoginOtp',
  async ({ mfaToken }, { getState, rejectWithValue }) => {
    try {
      const loginEmail = getState()?.auth?.mfa?.loginEmail || null;
      const response = await API.post(MFA_RESEND_PATH, {
        mfa_token: mfaToken,
      });
      notifyApiSuccessFrom(response, 'OTP resent successfully');
      return {
        challenge: extractMfaChallenge(
          {
            ...response.data,
            mfa_token: response.data?.mfa_token || mfaToken,
          },
          loginEmail
        ),
      };
    } catch (error) {
      const errorText = String(
        error.response?.data?.error || error.response?.data?.message || ''
      );
      return rejectWithValue({
        message: mfaErrorMessage(error, 'Failed to resend OTP'),
        status: error.response?.status,
        expired:
          error.response?.status === 401 &&
          /session expired|sign in again/i.test(errorText),
      });
    }
  }
);
export const refreshToken = createAsyncThunk('/auth/refresh-token', async (accessToken) => {
  try {
    const config = {
      headers: {
        "ngrok-skip-browser-warning": "69420",
        Authorization: `${accessToken}`,
        "Content-Type": "application/json",
      },
    };
    const response = await API.get('/auth/refresh-token', config);
    return response.data
    // if(response.data.user.is_admin){
    //   showSuccess('Login Successfully!');
    //   return response.data;
    // }else{
    //   showError('Login Failed!');
    // }

  } catch (error) {
    throw new Error(error);
  }
});

export const logoutUser = createAsyncThunk('/auth/logout', async () => {
  try {
    await API.delete('/auth/logout');

  } catch (error) {
    throw new Error('Failed to log out');
  }
});

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearMfaChallenge(state) {
      state.mfa = null;
    },
    applyAuthSession(state, action) {
      state.user = action.payload;
      state.mfa = null;
      state.error = null;
    },
    clearAuthUser(state) {
      state.user = null;
      state.mfa = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(registerUser.pending, (state) => {
        state.loading = true;
      })
      .addCase(registerUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
        state.error = null;
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.mfa = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload?.__mfa) {
          state.mfa = action.payload.challenge;
          state.user = null;
          state.error = null;
          return;
        }
        if (action.payload) {
          state.user = action.payload;
          state.mfa = null;
          state.error = null;
          if (action.payload.exp) {
            state.user.exp = action.payload.exp;
          }
        }
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(verifyLoginOtp.pending, (state) => {
        state.loading = true;
      })
      .addCase(verifyLoginOtp.fulfilled, (state, action) => {
        state.loading = false;
        state.mfa = null;
        if (action.payload) {
          state.user = action.payload;
          state.error = null;
          if (action.payload.exp) {
            state.user.exp = action.payload.exp;
          }
        }
      })
      .addCase(verifyLoginOtp.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
      })
      .addCase(resendLoginOtp.pending, (state) => {
        state.loading = true;
      })
      .addCase(resendLoginOtp.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload?.challenge?.token) {
          state.mfa = action.payload.challenge;
        }
        state.error = null;
      })
      .addCase(resendLoginOtp.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
      })
      .addCase(logoutUser.pending, (state) => {
        state.loading = true;
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state.loading = false;
        state.user = null;
        state.mfa = null;
        state.error = null;
      })
      .addCase(logoutUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })

      .addCase(refreshToken.fulfilled, (state, action) => {

        state.user.access_token = action.payload?.access_token;
        state.user.exp = action.payload?.exp;

      })
      .addCase(refreshToken.rejected, (state, action) => {
        state.loading = false;
        state.user = null;
        state.error = null;
      })

      .addCase(registerUserData.pending, (state) => {
        state.loading = true;
      })
      .addCase(registerUserData.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload; // Store the user data (full_name, phone, role, etc.)
        state.error = null;
      })
      .addCase(registerUserData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || action.error.message;
      });
  },
});

export const registerUserData = createAsyncThunk(
  "/users/signup-data",
  async (signupcpde, { rejectWithValue }) => {
    try {
      const response = await API.post("/users/signup-data", signupcpde);
      return response.data;
    } catch (error) {
      // Preserve the original error response
      return rejectWithValue({
        message: error.response?.data?.error || error.response?.data?.message || "Failed to get signup data",
        status: error.response?.status,
        data: error.response?.data
      });
    }
  }
);

export const { clearMfaChallenge, applyAuthSession, clearAuthUser } =
  authSlice.actions;

export default authSlice.reducer;
